import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FileText, 
  Shield, 
  Cookie, 
  RefreshCw, 
  Mail, 
  MapPin, 
  Phone,
  ArrowLeft,
  Scale,
  AlertTriangle
} from "lucide-react";

const COMPANY_INFO = {
  name: "MyHairChair, LLC",
  address: "Houston, Texas",
  email: "info@haircare.com",
  phone: "(713) 555-HAIR",
  website: "www.myhairchair.com"
};

const LegalPages = () => {
  const [activeTab, setActiveTab] = useState("terms");

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
      {/* Header */}
      <div className="bg-stone-900/80 border-b border-stone-800">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-stone-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-display text-white">Legal Information</h1>
          <p className="text-stone-400 mt-2">
            {COMPANY_INFO.name} • {COMPANY_INFO.address}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-stone-800/50 border border-stone-700 p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="terms" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" />
              Terms of Service
            </TabsTrigger>
            <TabsTrigger value="privacy" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Shield className="w-4 h-4 mr-2" />
              Privacy Policy
            </TabsTrigger>
            <TabsTrigger value="cookies" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Cookie className="w-4 h-4 mr-2" />
              Cookie Policy
            </TabsTrigger>
            <TabsTrigger value="refund" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refund Policy
            </TabsTrigger>
          </TabsList>

          {/* Terms of Service */}
          <TabsContent value="terms">
            <Card className="bg-stone-800/50 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-500" />
                  Terms of Service
                </CardTitle>
                <p className="text-stone-400 text-sm">Last Updated: January 13, 2026</p>
              </CardHeader>
              <CardContent className="prose prose-invert prose-stone max-w-none">
                <div className="space-y-6 text-stone-300">
                  <section>
                    <h3 className="text-xl font-semibold text-white">1. Acceptance of Terms</h3>
                    <p>
                      By accessing or using MyHairChair (&quot;the Platform&quot;), you agree to be bound by these Terms of Service. 
                      If you do not agree to these terms, please do not use our services.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">2. Description of Services</h3>
                    <p>
                      MyHairChair provides a platform connecting salon owners with independent beauty professionals 
                      for booth rental arrangements, appointment scheduling, and business management tools.
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Booth rental marketplace and applications</li>
                      <li>Appointment booking and management</li>
                      <li>Payment processing services</li>
                      <li>Marketing and client management tools</li>
                      <li>Business analytics and reporting</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">3. User Accounts</h3>
                    <p>
                      You must provide accurate, complete information when creating an account. You are responsible 
                      for maintaining the confidentiality of your account credentials and for all activities under your account.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">4. Booth Rental Agreements</h3>
                    <p>
                      MyHairChair facilitates connections between salon owners and renters but is not a party to 
                      any booth rental agreement. Users are responsible for:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Negotiating and finalizing rental terms directly</li>
                      <li>Compliance with all applicable laws and regulations</li>
                      <li>Maintaining appropriate business licenses and insurance</li>
                      <li>Adhering to salon rules and professional standards</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">5. Payment Terms</h3>
                    <p>
                      Payments processed through our platform are subject to our payment processor&apos;s terms. 
                      Platform fees and commissions are disclosed at the time of transaction.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">6. Prohibited Conduct</h3>
                    <p>Users may not:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Violate any applicable laws or regulations</li>
                      <li>Infringe on intellectual property rights</li>
                      <li>Harass, discriminate, or harm other users</li>
                      <li>Provide false or misleading information</li>
                      <li>Attempt to circumvent platform fees</li>
                      <li>Use the platform for unauthorized commercial purposes</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">7. Limitation of Liability</h3>
                    <p>
                      To the maximum extent permitted by law, MyHairChair shall not be liable for any indirect, 
                      incidental, special, consequential, or punitive damages arising from your use of the platform.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">8. Dispute Resolution</h3>
                    <p>
                      Any disputes arising from these terms shall be resolved through binding arbitration in 
                      Harris County, Texas, in accordance with Texas law.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">9. Modifications</h3>
                    <p>
                      We reserve the right to modify these terms at any time. Continued use of the platform 
                      after changes constitutes acceptance of the modified terms.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">10. Governing Law</h3>
                    <p>
                      These Terms shall be governed by and construed in accordance with the laws of the 
                      State of Texas, without regard to conflict of law principles.
                    </p>
                  </section>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Privacy Policy */}
          <TabsContent value="privacy">
            <Card className="bg-stone-800/50 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-500" />
                  Privacy Policy
                </CardTitle>
                <p className="text-stone-400 text-sm">Last Updated: January 13, 2026</p>
              </CardHeader>
              <CardContent className="prose prose-invert prose-stone max-w-none">
                <div className="space-y-6 text-stone-300">
                  <section>
                    <h3 className="text-xl font-semibold text-white">1. Information We Collect</h3>
                    <p>We collect the following types of information:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Personal Information:</strong> Name, email, phone number, address</li>
                      <li><strong>Professional Information:</strong> License numbers, certifications, experience</li>
                      <li><strong>Financial Information:</strong> Payment details, transaction history</li>
                      <li><strong>Usage Data:</strong> How you interact with our platform</li>
                      <li><strong>Device Information:</strong> Browser type, IP address, device identifiers</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">2. How We Use Your Information</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>To provide and improve our services</li>
                      <li>To process transactions and send related information</li>
                      <li>To connect salon owners with potential renters</li>
                      <li>To send promotional communications (with your consent)</li>
                      <li>To comply with legal obligations</li>
                      <li>To protect against fraudulent or illegal activity</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">3. Information Sharing</h3>
                    <p>We may share your information with:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Other users as necessary to facilitate connections</li>
                      <li>Service providers who assist in our operations</li>
                      <li>Payment processors for transaction processing</li>
                      <li>Law enforcement when required by law</li>
                    </ul>
                    <p className="mt-4">
                      We do NOT sell your personal information to third parties.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">4. Data Security</h3>
                    <p>
                      We implement industry-standard security measures including encryption, secure servers, 
                      and regular security audits to protect your information.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">5. Your Rights</h3>
                    <p>You have the right to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Access your personal information</li>
                      <li>Correct inaccurate information</li>
                      <li>Request deletion of your data</li>
                      <li>Opt-out of marketing communications</li>
                      <li>Export your data in a portable format</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">6. Data Retention</h3>
                    <p>
                      We retain your information for as long as your account is active or as needed to provide 
                      services. We may retain certain information as required by law or for legitimate business purposes.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">7. Children&apos;s Privacy</h3>
                    <p>
                      Our services are not intended for individuals under 18 years of age. We do not knowingly 
                      collect information from minors.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">8. California Privacy Rights</h3>
                    <p>
                      California residents have additional rights under the CCPA, including the right to know 
                      what personal information is collected, request deletion, and opt-out of data sales.
                    </p>
                  </section>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cookie Policy */}
          <TabsContent value="cookies">
            <Card className="bg-stone-800/50 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-amber-500" />
                  Cookie Policy
                </CardTitle>
                <p className="text-stone-400 text-sm">Last Updated: January 13, 2026</p>
              </CardHeader>
              <CardContent className="prose prose-invert prose-stone max-w-none">
                <div className="space-y-6 text-stone-300">
                  <section>
                    <h3 className="text-xl font-semibold text-white">What Are Cookies?</h3>
                    <p>
                      Cookies are small text files stored on your device when you visit our website. 
                      They help us provide a better user experience and understand how our platform is used.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">Types of Cookies We Use</h3>
                    <div className="space-y-4">
                      <div className="p-4 bg-stone-700/50 rounded-lg">
                        <h4 className="font-semibold text-amber-400">Essential Cookies</h4>
                        <p className="text-sm">Required for the platform to function. Cannot be disabled.</p>
                      </div>
                      <div className="p-4 bg-stone-700/50 rounded-lg">
                        <h4 className="font-semibold text-amber-400">Performance Cookies</h4>
                        <p className="text-sm">Help us understand how visitors use our platform.</p>
                      </div>
                      <div className="p-4 bg-stone-700/50 rounded-lg">
                        <h4 className="font-semibold text-amber-400">Functional Cookies</h4>
                        <p className="text-sm">Remember your preferences and settings.</p>
                      </div>
                      <div className="p-4 bg-stone-700/50 rounded-lg">
                        <h4 className="font-semibold text-amber-400">Marketing Cookies</h4>
                        <p className="text-sm">Used to deliver relevant advertisements.</p>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">Managing Cookies</h3>
                    <p>
                      You can control cookies through your browser settings. Note that disabling certain 
                      cookies may affect the functionality of our platform.
                    </p>
                  </section>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Refund Policy */}
          <TabsContent value="refund">
            <Card className="bg-stone-800/50 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-amber-500" />
                  Refund Policy
                </CardTitle>
                <p className="text-stone-400 text-sm">Last Updated: January 13, 2026</p>
              </CardHeader>
              <CardContent className="prose prose-invert prose-stone max-w-none">
                <div className="space-y-6 text-stone-300">
                  <section>
                    <h3 className="text-xl font-semibold text-white">Platform Subscription Refunds</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Monthly subscriptions may be cancelled at any time</li>
                      <li>Refunds for unused portions are available within 7 days of purchase</li>
                      <li>Annual subscriptions may be refunded on a prorated basis within 30 days</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">Booth Rental Payments</h3>
                    <p>
                      Refunds for booth rental payments are subject to the individual salon&apos;s policies. 
                      MyHairChair facilitates payments but does not guarantee refunds for rental disputes.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">Appointment Cancellations</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Cancellation policies are set by individual stylists</li>
                      <li>Deposits may be non-refundable based on stylist policy</li>
                      <li>No-shows may result in forfeiture of prepaid amounts</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold text-white">How to Request a Refund</h3>
                    <p>Contact us at {COMPANY_INFO.email} with:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Your account email</li>
                      <li>Transaction details</li>
                      <li>Reason for refund request</li>
                    </ul>
                    <p className="mt-4">
                      Refund requests are typically processed within 5-10 business days.
                    </p>
                  </section>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Contact Section */}
        <Card className="bg-stone-800/50 border-stone-700 mt-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Scale className="w-5 h-5 text-amber-500" />
              Legal Inquiries & Complaints
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-amber-500 mt-1" />
                <div>
                  <p className="text-white font-medium">Email</p>
                  <a href={`mailto:${COMPANY_INFO.email}`} className="text-amber-400 hover:underline">
                    {COMPANY_INFO.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-amber-500 mt-1" />
                <div>
                  <p className="text-white font-medium">Headquarters</p>
                  <p className="text-stone-400">{COMPANY_INFO.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-1" />
                <div>
                  <p className="text-white font-medium">Report Issues</p>
                  <p className="text-stone-400">For complaints or legal matters</p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-amber-200 text-sm">
                <strong>Notice:</strong> For formal legal complaints, please send written correspondence to 
                {COMPANY_INFO.email} with &quot;Legal Complaint&quot; in the subject line. We aim to respond 
                to all legal inquiries within 5 business days.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LegalPages;
